import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { debounceTime, Subject } from 'rxjs';
import { validatePassword } from 'src/app/constants/auth-config.const';
import { CHECK_EMAIL, TEAM_LIST, TEAM_MENU } from 'src/app/constants/endpoint.const';
import { ROLE_LIST } from 'src/app/constants/team.const';
import { BaseService } from 'src/app/services/base.service';
import { FormValidationService } from 'src/app/services/form-validation.service';
import { TriggerService } from 'src/app/services/trigger.service';
import { setRegex, setRegexName, preventNonNumeric } from 'src/app/libs/functions/common.functions';
import { roleList, formEdit } from 'src/app/types/request/team.type';
import { menuListResponse, menuListType, menuStructure, menuStructureType } from 'src/app/models/teams.model';
import { accessType, TeamsListIdResponse } from 'src/app/types/response/teams.response.type';

@Component({
  selector: 'app-add-team',
  standalone: false,
  templateUrl: './add-team.component.html',
  styleUrl: './add-team.component.scss',
})
export class AddTeamComponent implements OnInit {
  private route = inject(Router);
  private fb = inject(FormBuilder);
  private formValidation = inject(FormValidationService);
  private baseService = inject(BaseService);
  private message = inject(NzMessageService);
  private triggerService = inject(TriggerService);
  private activeRoute = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  setRegex = setRegex;
  setRegexName = setRegexName;
  preventNonNumeric = preventNonNumeric;
  isEditMode = signal(false);
  idTeam = signal(0);
  existingEmail = signal('');
  loading = signal(true);
  validName = signal(true);
  errMessName = signal('');
  validEmail = signal(true);
  errMessEmail = signal('');
  roleUser = signal('');
  roleList: roleList[] = ROLE_LIST;

  isShowPassword = signal(false);
  isShowPassword2 = signal(false);
  isShowPin = signal(false);
  isShowPin2 = signal(false);
  validationPassword = validatePassword;
  visibleValPass = signal(false);
  isValidPass = signal(true);
  errMessPass = signal('');
  errMessPass2 = signal('');
  visiblePin = signal(false);
  isValidPin = signal(true);
  errMessPin = signal('');
  visiblePin2 = signal(false);
  isValidPin2 = signal(true);
  errMessPin2 = signal('');

  eyeOn =
    'https://storage.googleapis.com/komerce/assets/komerce-icon/Hitam/eye.svg';
  eyeOff =
    'https://storage.googleapis.com/komerce/assets/komerce-icon/Hitam/eye-slash.svg';
  checked = signal(false);
  isVerifTeam = signal(false);
  emailTeam = signal('');
  emailCheckSubject = new Subject<string>();
  isClosedSidebar = signal(false);

  menuList = signal<menuListType[]>([]);
  menuStructure: menuStructureType[] = [];
  pendingTeamData: TeamsListIdResponse | null = null;

  addTeamForm: FormGroup = this.fb.group(
    {
      username: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(50),
        ],
      ],
      email: ['', [Validators.required, Validators.email]],
      role: [null, [Validators.required]],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(32),
        ],
      ],
      confirmPassword: [
        '',
        [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(32),
        ],
      ],
      pin: [
        null,
        [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
      ],
      confirmPin: [
        null,
        [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
      ],
      allAccess: [false],
    },
    {
      validators: Validators.compose([
        this.matchFieldsValidator(
          'password',
          'confirmPassword',
          'passwordMismatch'
        ),
        this.matchFieldsValidator('pin', 'confirmPin', 'pinMismatch'),
      ]),
    }
  );

  ngOnInit(): void {
    setTimeout(() => {
      this.loading.set(false);
    }, 1000);

    this.getMenuList();

    this.activeRoute.params.subscribe((params) => {
      if (params['id']) {
        this.isEditMode.set(true);
        this.idTeam.set(Number(params['id']));
        this.getTeam();
      }
    });

    this.initAllAccessSync();
    this.triggerService.triggerParent$.subscribe(() => {
      this.addTeam();
    });

    this.triggerService.triggerSidebar$.subscribe(() => {
      this.isClosedSidebar.update(v => !v);
    });

    this.emailCheckSubject
      .pipe(debounceTime(1000), takeUntilDestroyed(this.destroyRef))
      .subscribe((email) => {
        const params = { email };
        this.baseService
          .postData(CHECK_EMAIL, params)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (res) => {
              if (!res.data.exist) {
                this.validEmail.set(true);
                this.errMessEmail.set('');
              } else {
                this.validEmail.set(false);
                this.errMessEmail.set('Email has been registered to an existing user');
              }
            },
            error: (err) => {
              this.message.error(err.error.meta.message);
            },
          });
      });
  }

  back() {
    this.route.navigate(['/teams']);
  }

  getMenuList() {
    this.baseService.getData(TEAM_MENU).subscribe({
      next: (res) => {
        this.menuList.set(res.data);
        const structure = this.generateMenuStructure(res.data);
        this.menuStructure = structure;

        // Add form controls dynamically
        res.data.forEach((item: menuListType) => {
          this.addTeamForm.addControl(`menu_${item.key}`, this.fb.control(false));
        });

        // Set validators that depend on the menu keys
        const menuKeys = res.data.map((item: menuListType) => `menu_${item.key}`);
        this.addTeamForm.addValidators(this.atLeastOneMenuCheckedValidator(menuKeys));
        this.addTeamForm.updateValueAndValidity();

        this.initAllAccessSync();

        // If we had team data waiting for the menu to load, patch it now
        if (this.pendingTeamData) {
          this.patchMenuAccess(this.pendingTeamData);
          this.pendingTeamData = null;
        }
      },
      error: (err) => {
        this.message.error(err.error.meta.message);
      },
    });
  }

  generateMenuStructure(data: menuListResponse[]): menuStructureType[] {
    const labelOverrides: { [key: string]: string } = {
      'shipping_order_data': 'Order Data',
      'shipping_issue': 'Issue',
      'shipping_rates_track': 'Rates & Track',
      'shipping_simulator': 'Simulator',
      'qrisly': 'QRISLY',
      'payment_invoices': 'Invoices',
      'developer_settings': 'Settings',
      'subscriptions': 'Subscriptions',
    };

    const categories: { [key: string]: {key: string, label: string, value: number, rawKey: string}[] } = {};
    data.forEach(item => {
      if (!categories[item.category]) {
        categories[item.category] = [];
      }
      categories[item.category].push({
        key: `menu_${item.key}`,
        label: labelOverrides[item.key] || item.name,
        value: item.id,
        rawKey: item.key
      });
    });

    const col1Cats = ['Dashboard', 'Shipping API', 'Payment API', 'Finance'];
    const col2Cats = ['Subscriptions', 'Developer', 'Account'];

    const createGroups = (catNames: string[]) => {
      return catNames.filter(name => categories[name]).map(name => {
        const group: menuStructure = { title: name };

        if (name === 'Developer') {
          group.layout = 'two-column';
          const items = categories[name];
          const col1Keys = ['webhook', 'developer_settings'];
          const col2Keys = ['document', 'api_docs_plugin'];

          group.columns = [
            items.filter(i => col1Keys.includes(i.rawKey)),
            items.filter(i => col2Keys.includes(i.rawKey)).reverse()
          ];
        } else {
          group.items = categories[name];
        }
        return group;
      });
    };

    return [
      { col: 1, groups: createGroups(col1Cats) },
      { col: 2, groups: createGroups(col2Cats) }
    ];
  }

  setRegexPassword(event: KeyboardEvent) {
    return this.formValidation.validatePasswordInput(event);
  }

  validateName() {
    const value = this.addTeamForm.get('username')?.value || '';
    const isError = value === '' || value.length < 3 || value.length > 50;
    this.validName.set(!isError);
    this.errMessName.set(
      value === '' ? 'Name must be filled in' :
      value.length < 3 ? 'Minimum 3 characters' :
      value.length > 50 ? 'Maximum 50 characters' : ''
    );
  }

  validateEmail() {
    const value = this.addTeamForm.get('email')?.value || '';
    const result = this.formValidation.validateEmail(value);
    this.validEmail.set(result.valid);
    this.errMessEmail.set(result.message);

    if (result.valid && (this.existingEmail() !== value || !this.isEditMode())) {
      this.emailCheckSubject.next(value);
    }
  }

  validatePassword() {
    this.visibleValPass.set(true);
    const { password: v1, confirmPassword: v2 } = this.addTeamForm.value;
    const result = this.formValidation.validatePassword(v1 || '', this.validationPassword);

    this.isValidPass.set(result.isValid);
    this.errMessPass.set(!v1 ? 'Password must be filled' : result.isValid ? '' : 'Password does not comply with the provisions');
    this.errMessPass2.set(v1 !== v2 && v2 ? 'Make sure the password confirmation is the same as the previous password.' : '');

    this.addTeamForm.get('password')?.setErrors(result.errors.length ? { passwordInvalid: result.errors.join(', ') } : null);
  }

  confirmPassword() {
    const { password: v1, confirmPassword: v2 } = this.addTeamForm.value;
    this.errMessPass2.set(
      v1 !== v2 && v2 ? 'Make sure the password confirmation is the same as the previous password.' :
      !v2 ? 'Password confirmation must be filled in' : ''
    );
  }

  validateInputPin() {
    const { pin: v1, confirmPin: v2 } = this.addTeamForm.value;
    if (v2) this.validateInputConfirmPin();
    const result = this.formValidation.validatePin(v1 || '');
    this.isValidPin.set(result.valid);
    this.errMessPin.set(result.message);
  }

  validateInputConfirmPin() {
    const { pin: v1, confirmPin: v2 } = this.addTeamForm.value;
    const result = this.formValidation.validateConfirmPin(v2 || '', v1 || '');
    this.isValidPin2.set(result.valid);
    this.errMessPin2.set(result.message);
  }

  initAllAccessSync() {
    const individualKeys = this.menuList().map(item => `menu_${item.key}`);

    individualKeys.forEach((key) => {
      this.addTeamForm.get(key)?.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          const allChecked = individualKeys.every(k => this.addTeamForm.get(k)?.value);
          this.addTeamForm.get('allAccess')?.setValue(allChecked, { emitEvent: false });
        });
    });

    this.addTeamForm.get('allAccess')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: boolean) => {
        individualKeys.forEach(key => this.addTeamForm.get(key)?.setValue(value, { emitEvent: false }));
      });
  }

  atLeastOneMenuCheckedValidator(menuKeys: string[]): ValidatorFn {
    return (form: AbstractControl): ValidationErrors | null => {
      const isAnyChecked = menuKeys.some(
        (key) => form.get(key)?.value === true
      );
      return isAnyChecked ? null : { atLeastOneMenu: true };
    };
  }

  getSelectedMenus(): { menu: { id: number; access: boolean }[] } {
    const formValue = this.addTeamForm.value;
    const allAccess = formValue.allAccess;

    const menu = this.menuList().map((item) => ({
      id: item.id,
      access: allAccess ? true : !!formValue[`menu_${item.key}`],
    }));
    return { menu };
  }

  matchFieldsValidator(
    field1: string,
    field2: string,
    errorKey: string
  ): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const value1 = group.get(field1)?.value;
      const value2 = group.get(field2)?.value;

      if (value1 !== value2) {
        group.get(field2)?.setErrors({ [errorKey]: true });
        return { [errorKey]: true };
      } else {
        const errors = group.get(field2)?.errors;
        if (errors) {
          delete errors[errorKey];
          if (Object.keys(errors).length === 0) {
            group.get(field2)?.setErrors(null);
          } else {
            group.get(field2)?.setErrors(errors);
          }
        }
        return null;
      }
    };
  }

  get isFormInvalid(): boolean {
    return (
      this.addTeamForm.invalid ||
      !!this.addTeamForm.errors?.['atLeastOneMenu'] ||
      !this.validEmail() ||
      !this.validName() ||
      !this.isValidPass() ||
      !this.isValidPin()
    );
  }

  saveTeam() {
    if (this.isEditMode()) {
      this.editTeam();
    } else {
      this.emailTeam.set(this.addTeamForm.get('email')?.value);
      this.isVerifTeam.set(true);
    }
  }

  private getTeamParams() {
    const { username, email, password, role, pin } = this.addTeamForm.getRawValue();
    return {
      name: username,
      email,
      password,
      role,
      pin,
      ...this.getSelectedMenus()
    };
  }

  addTeam(): void {
    this.baseService
      .postData(TEAM_LIST, this.getTeamParams())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.message.success("Add Team Success");
          this.route.navigate(['/teams']);
        },
        error: (err) => this.message.error(err.error.meta.message)
      });
  }

  getTeam() {
    this.baseService
      .getData(`${TEAM_LIST}/${this.idTeam()}`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: {data: TeamsListIdResponse}) => {
          this.addTeamForm.patchValue({
            username: res.data.name,
            email: res.data.email,
            role: res.data.role,
            pin: res.data.pin,
            confirmPin: res.data.pin,
          });
          this.existingEmail.set(res.data.email);

          if (this.menuList().length > 0) {
            this.patchMenuAccess(res.data);
          } else {
            this.pendingTeamData = res.data;
          }
        },
        error: (err) => this.message.error(err.error.meta.message)
      });
  }

  patchMenuAccess(data: TeamsListIdResponse) {
    if (data.access?.length > 0) {
      const menuPatch: Record<string, boolean> = {};
      this.menuList().forEach(menuItem => {
        const found = data.access.find((a: accessType) => a.id === menuItem.id);
        menuPatch[`menu_${menuItem.key}`] = found ? found.access : false;
      });
      this.addTeamForm.patchValue(menuPatch);
      this.initAllAccessSync();
    }
  }

  editTeam(): void {
    this.baseService
      .putData(`${TEAM_LIST}/${this.idTeam()}`, this.getTeamParams())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.message.success("Edit Team Success");
          this.route.navigate(['/teams']);
        },
        error: (err) => this.message.error(err.error.meta.message)
      });
  }

  resetData(): void {
    this.addTeamForm.reset();
  }
}
